import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { FiTrash2, FiUpload } from 'react-icons/fi';
import { createNotification } from '../notifications/notifications';
import {
  PORTFOLIO_FILE_LIMITS,
  downloadFile,
  fileUrl,
  removeFile,
  uploadFile,
  validateFile,
} from '../../actions/portfolio';
import { formatDate, formatSize, fileKind } from './format';
import styles from './styles.module.css';

/* Вкладка «Прикреплённые материалы» (п. 8 ТЗ).
   Сверху «Загрузить файл» и ограничения, ниже список в общей рамке: значок
   вида файла, имя ссылкой на скачивание (новая вкладка), размер и дата,
   корзина. Удаление — через окно подтверждения с именем файла (родитель).
   Файл проверяется до отправки: сверх предельного размера или недопустимого
   вида не отправляется, выводится сообщение с указанием ограничения. */

const limitsNote = () => {
  const { maxLabel, extensions } = PORTFOLIO_FILE_LIMITS;
  return `Не более ${maxLabel}. Допустимые виды: ${extensions.map((ext) => ext.toUpperCase()).join(', ')}.`;
};

// порядок сервером не задан — новые сверху, при равной дате по имени
const sortFiles = (files) => [...files].sort((a, b) => {
  const byTime = String(b.time || '').localeCompare(String(a.time || ''));
  return byTime || String(a.file || '').localeCompare(String(b.file || ''), 'ru');
});

const FilesTab = ({ confirm, toast }) => {
  const dispatch = useDispatch();
  const files = useSelector((state) => state.portfolio.files);
  const mid = useSelector((state) => state.auth.mid);
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const pickFile = () => {
    if (!inputRef.current) return;
    inputRef.current.value = '';
    inputRef.current.click();
  };

  const handleChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const problem = validateFile(file);
    if (problem) {
      createNotification('error', problem, 'Файл не загружен');
      return;
    }
    setUploading(true);
    const result = await dispatch(uploadFile(file));
    setUploading(false);
    if (result.error) {
      createNotification('error', result.error, 'Файл не загружен');
      return;
    }
    toast('Файл загружен');
  };

  const handleDownload = (file) => async (e) => {
    e.preventDefault();
    const result = await dispatch(downloadFile(file.urlencoded, file.file));
    if (result.error) createNotification('error', result.error, 'Скачивание');
  };

  const handleRemove = (file) => () => {
    confirm({
      title: 'Удалить файл?',
      text: `Файл «${file.file}» будет удалён из портфолио. Отменить удаление нельзя.`,
      onYes: async () => {
        setDeleting(file.urlencoded);
        const result = await dispatch(removeFile(file.urlencoded, file.file));
        setDeleting(null);
        if (result.error) {
          createNotification('error', result.error, 'Файл не удалён');
          return;
        }
        toast('Файл удалён');
      },
    });
  };

  const meta = (file) => [formatSize(file.size), formatDate(file.time)].filter(Boolean).join(' · ');

  return (
    <>
      <div className={styles.upl}>
        <button type="button" className={styles.btn} onClick={pickFile} disabled={uploading}>
          <FiUpload size={17} />
          {uploading ? 'Загрузка…' : 'Загрузить файл'}
        </button>
        <span className={styles.note}>{limitsNote()}</span>
        <input
          ref={inputRef}
          type="file"
          className={styles.hiddenInput}
          accept={PORTFOLIO_FILE_LIMITS.extensions.map((ext) => `.${ext}`).join(',')}
          onChange={handleChange}
        />
      </div>

      <div className={styles.files}>
        {files === null && (
          <div className={styles.loader}>
            <Spinner animation="border" role="status"><span className="sr-only">Загрузка…</span></Spinner>
          </div>
        )}
        {files !== null && files.length === 0 && (
          <div className={styles.emptyRow}>Файлы к портфолио не прикреплены</div>
        )}
        {files !== null && sortFiles(files).map((file) => (
          <div className={styles.file} key={file.urlencoded || file.file}>
            <span className={styles.fileIcon}>{fileKind(file.file)}</span>
            <a
              className={styles.fileName}
              href={fileUrl(mid, file.urlencoded)}
              target="_blank"
              rel="noopener noreferrer"
              title={file.file}
              onClick={handleDownload(file)}
            >
              {file.file}
            </a>
            <span className={styles.fileMeta}>{meta(file)}</span>
            <button
              type="button"
              className={styles.del}
              title="Удалить файл"
              disabled={deleting === file.urlencoded}
              onClick={handleRemove(file)}
            >
              <FiTrash2 size={17} />
            </button>
          </div>
        ))}
      </div>
    </>
  );
};

export default FilesTab;
